// ─── Workflow Definition Admin API Service ───
// Follows patterns from templateManagementService.ts

// ─── Types ───

export type WorkflowStatus = 'draft' | 'under_review' | 'validated' | 'published' | 'archived';
export type JurisdictionScope = 'state' | 'county' | 'federal' | 'tribal';
export type WorkflowTaskType = 'filing' | 'document_prep' | 'review' | 'hearing' | 'deadline' | 'communication' | 'administrative' | 'service' | 'discovery' | 'negotiation';
export type WorkflowPriority = 'low' | 'medium' | 'high' | 'critical';

export interface WorkflowDefinition {
  id: string;
  realm_id: string;
  state_code: string;
  practice_area: string;
  case_subtype: string;
  label: string;
  description?: string | null;
  source_authority?: string | null;
  source_authority_url?: string | null;
  applies_to_states: string[];
  jurisdiction_scope: JurisdictionScope;
  jurisdiction_code?: string | null;
  parent_definition_id?: string | null;
  version: number;
  status: WorkflowStatus;
  confidence_score?: number | null;
  authored_by?: string | null;
  reviewed_by?: string | null;
  approved_by?: string | null;
  published_at?: string | null;
  last_validated_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkflowPhase {
  id: string;
  workflow_definition_id: string;
  name: string;
  description?: string | null;
  phase_order: number;
  estimated_duration_days?: number | null;
  is_court_mandated: boolean;
  mandate_citation?: string | null;
  created_at: string;
  updated_at: string;
  tasks?: WorkflowTask[];
}

export interface WorkflowTask {
  id: string;
  workflow_phase_id: string;
  title: string;
  description?: string | null;
  task_type: WorkflowTaskType;
  task_order: number;
  priority: WorkflowPriority;
  due_date_offset_days: number;
  due_date_calculation_type: string;
  is_court_mandated: boolean;
  mandate_type?: string | null;
  statute_citation?: string | null;
  statute_url?: string | null;
  rule_citation?: string | null;
  notes?: string | null;
  assignee_role?: string | null;
  creates_calendar_event: boolean;
  calendar_event_type?: string | null;
  calendar_duration_minutes?: number | null;
  service_method?: string | null;
  service_on_parties?: string[] | null;
  service_proof_required: boolean;
  estimated_hours?: number | null;
  is_billable: boolean;
  created_at: string;
  updated_at: string;
  forms?: WorkflowTaskForm[];
}

export interface WorkflowTaskForm {
  id: string;
  workflow_task_id: string;
  court_form_id?: string | null;
  form_number: string;
  form_name?: string | null;
  is_mandatory: boolean;
  filing_sequence?: number | null;
  notes?: string | null;
  created_at: string;
}

export interface WorkflowDefinitionWithRelations extends WorkflowDefinition {
  phases: (WorkflowPhase & { tasks: (WorkflowTask & { forms: WorkflowTaskForm[] })[] })[];
}

export interface WorkflowAuditLogEntry {
  id: string;
  workflow_definition_id?: string | null;
  action: string;
  actor_user_id?: string | null;
  actor_name?: string | null;
  entity_type: string;
  entity_id?: string | null;
  changes: Record<string, unknown>;
  notes?: string | null;
  created_at: string;
}

export interface CoverageSubtypeHealth {
  total_tasks: number;
  cited_tasks: number;
  mandated_tasks: number;
  mandated_cited: number;
}

export interface CoverageSubtype {
  id: string;
  case_subtype: string;
  label: string;
  status: WorkflowStatus;
  version: number;
  citation_health: CoverageSubtypeHealth;
}

export interface CoverageCell {
  state_code: string;
  practice_area: string;
  total_subtypes: number;
  published_count: number;
  draft_count: number;
  under_review_count: number;
  validated_count: number;
  status: 'complete' | 'partial' | 'none';
  subtypes: CoverageSubtype[];
}

export interface CitationStats {
  total_tasks: number;
  cited_tasks: number;
  mandated_tasks: number;
  mandated_cited: number;
}

export interface WorkflowDefinitionWithStats extends WorkflowDefinition {
  citation_stats?: CitationStats;
}

export interface WorkflowListResponse {
  items: WorkflowDefinitionWithStats[];
  total: number;
}

export interface WorkflowListFilters {
  state_code?: string;
  practice_area?: string;
  status?: WorkflowStatus;
  page?: number;
  limit?: number;
}

// ─── Helpers ───

const API_BASE = '/api/v1/workflows/admin';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('webelieve_access_token');
  return {
    Authorization: token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json',
  };
}

async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const text = await response.text();
    throw new Error(`Unexpected response (${response.status}): ${text.slice(0, 200)}`);
  }
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.description || data.message || data.error || `Request failed with status ${response.status}`);
  }
  return data as T;
}

function toQuery(params: Record<string, string | number | undefined>): string {
  const pairs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return pairs.length > 0 ? `?${pairs.join('&')}` : '';
}

// ─── Case Subtypes ───

export interface CaseSubtype {
  id: string;
  state_code: string;
  practice_area: string;
  subtype_key: string;
  label: string;
  description?: string | null;
  court_form_ref?: string | null;
  statute_ref?: string | null;
  is_common: boolean;
  display_order: number;
  // Section 5 — optional resolver-backed taxonomy metadata. Existing consumers
  // that only read the seven fields above continue to work.
  canonical_key?: string;
  mapping_id?: string | null;
  local_key?: string | null;
  local_label?: string | null;
  source_scope?: 'court' | 'state' | 'national' | 'legacy' | 'json';
  resolution_status?: 'validated' | 'legacy_fallback' | 'json_fallback';
  confidence_score?: number | null;
  source_url?: string | null;
  warnings?: string[];
}

export async function getCaseSubtypes(
  stateCode: string,
  practiceArea: string,
  jurisdictionCode?: string,
): Promise<CaseSubtype[]> {
  const params: Record<string, string> = { state_code: stateCode, practice_area: practiceArea };
  if (jurisdictionCode) params.jurisdiction_code = jurisdictionCode;
  const response = await fetch(`${API_BASE}/case-subtypes${toQuery(params)}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const data = await parseResponse<{ subtypes: CaseSubtype[] }>(response);
  return data.subtypes;
}

// ─── AI-Assisted Source Authority Fetch ───

export interface SourceAuthorityAlternative {
  url: string;
  title: string;
  snippet: string;
}

export interface SourceAuthorityResult {
  source_authority: string;
  source_authority_url: string;
  description: string;
  confidence: number;
  alternatives: SourceAuthorityAlternative[];
}

export async function fetchSourceAuthority(dto: {
  state_code: string;
  practice_area: string;
  case_subtype: string;
  label: string;
}): Promise<SourceAuthorityResult> {
  const response = await fetch(`${API_BASE}/fetch-source-authority`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(dto),
  });
  return parseResponse<SourceAuthorityResult>(response);
}

// ─── AI Workflow Generation ───

export interface SuggestedForm {
  form_number: string;
  form_name: string;
  court_form_id: string | null;
  is_mandatory: boolean;
  pdf_url: string | null;
  in_catalog: boolean;
  source_url: string | null;
}

export interface SuggestedTask {
  title: string;
  description: string;
  task_type: string;
  priority: string;
  due_date_offset_days: number;
  is_court_mandated: boolean;
  statute_citation: string | null;
  statute_url: string | null;
  is_required: boolean;
  forms: SuggestedForm[];
  reasoning: string | null;
  deadline_basis: 'statutory' | 'common_practice' | 'estimated' | null;
}

export interface SuggestedPhase {
  name: string;
  description: string;
  estimated_duration_days: number;
  is_court_mandated: boolean;
  mandate_citation: string | null;
  source_url: string | null;
  is_required: boolean;
  tasks: SuggestedTask[];
  confidence: 'high' | 'medium' | 'low';
  confidence_reason: string | null;
}

export interface WorkflowContextSummary {
  forms_in_database: number;
  forms_matched: number;
  forms_ai_suggested: number;
  online_sources_found: number;
  curated_blueprint_available: boolean;
  curated_blueprint_jurisdiction: string | null;
  statute_ref: string | null;
  user_description_provided: boolean;
  sources_consulted: Array<{ title: string; url: string }>;
}

export interface WorkflowSuggestion {
  source_authority: string;
  source_authority_url: string;
  description: string;
  confidence: number;
  source: 'curated' | 'ai_generated' | 'hybrid';
  phases: SuggestedPhase[];
  context_summary?: WorkflowContextSummary;
}

export async function generateWorkflowSuggestion(dto: {
  state_code: string;
  practice_area: string;
  case_subtype: string;
  label: string;
  description?: string;
  source_authority?: string;
  source_authority_url?: string;
  jurisdiction_scope?: string;
}): Promise<WorkflowSuggestion> {
  const response = await fetch(`${API_BASE}/generate-workflow`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(dto),
  });
  return parseResponse<WorkflowSuggestion>(response);
}

export async function applyWorkflowSuggestion(
  definitionId: string,
  phases: SuggestedPhase[],
): Promise<void> {
  const response = await fetch(`${API_BASE}/definitions/${definitionId}/apply-suggestion`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ phases }),
  });
  await parseResponse<{ success: boolean }>(response);
}

// ─── API Functions ───

export async function listWorkflowDefinitions(
  filters: WorkflowListFilters = {},
): Promise<WorkflowListResponse> {
  const response = await fetch(`${API_BASE}/definitions${toQuery(filters as any)}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return parseResponse<WorkflowListResponse>(response);
}

export async function getWorkflowDefinition(
  definitionId: string,
): Promise<WorkflowDefinitionWithRelations> {
  const response = await fetch(`${API_BASE}/definitions/${definitionId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const data = await parseResponse<{ definition: WorkflowDefinitionWithRelations }>(response);
  return data.definition;
}

export async function createWorkflowDefinition(
  dto: {
    state_code: string;
    practice_area: string;
    case_subtype: string;
    label: string;
    description?: string;
    source_authority?: string;
    source_authority_url?: string;
    applies_to_states?: string[];
    jurisdiction_scope?: JurisdictionScope;
    jurisdiction_code?: string;
  },
): Promise<WorkflowDefinition> {
  const response = await fetch(`${API_BASE}/definitions`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(dto),
  });
  const data = await parseResponse<{ definition: WorkflowDefinition }>(response);
  return data.definition;
}

export async function updateWorkflowDefinition(
  definitionId: string,
  dto: Partial<{
    label: string;
    description: string;
    source_authority: string;
    source_authority_url: string;
    applies_to_states: string[];
    jurisdiction_scope: JurisdictionScope;
    jurisdiction_code: string;
  }>,
): Promise<WorkflowDefinition> {
  const response = await fetch(`${API_BASE}/definitions/${definitionId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(dto),
  });
  const data = await parseResponse<{ definition: WorkflowDefinition }>(response);
  return data.definition;
}

export async function archiveWorkflowDefinition(definitionId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/definitions/${definitionId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  await parseResponse<{ success: boolean }>(response);
}

export async function restoreWorkflowDefinition(definitionId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/definitions/${definitionId}/restore`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  await parseResponse<{ success: boolean }>(response);
}

export async function permanentDeleteWorkflowDefinition(definitionId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/definitions/${definitionId}/permanent`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  await parseResponse<{ success: boolean }>(response);
}

// ─── Phases ───

export async function addPhase(
  definitionId: string,
  dto: { name: string; description?: string; estimated_duration_days?: number; is_court_mandated?: boolean; mandate_citation?: string },
): Promise<WorkflowPhase> {
  const response = await fetch(`${API_BASE}/definitions/${definitionId}/phases`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(dto),
  });
  const data = await parseResponse<{ phase: WorkflowPhase }>(response);
  return data.phase;
}

export async function updatePhase(
  phaseId: string,
  dto: Partial<{ name: string; description: string; estimated_duration_days: number; is_court_mandated: boolean; mandate_citation: string }>,
): Promise<WorkflowPhase> {
  const response = await fetch(`${API_BASE}/phases/${phaseId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(dto),
  });
  const data = await parseResponse<{ phase: WorkflowPhase }>(response);
  return data.phase;
}

export async function deletePhase(phaseId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/phases/${phaseId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  await parseResponse<{ success: boolean }>(response);
}

export async function reorderPhases(definitionId: string, phaseIds: string[]): Promise<void> {
  const response = await fetch(`${API_BASE}/definitions/${definitionId}/reorder-phases`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ phase_ids: phaseIds }),
  });
  await parseResponse<{ success: boolean }>(response);
}

// ─── Tasks ───

export async function addTask(
  phaseId: string,
  dto: {
    title: string;
    description?: string;
    task_type?: WorkflowTaskType;
    priority?: WorkflowPriority;
    due_date_offset_days?: number;
    due_date_calculation_type?: string;
    is_court_mandated?: boolean;
    mandate_type?: string;
    statute_citation?: string;
    statute_url?: string;
    rule_citation?: string;
    notes?: string;
    assignee_role?: string;
    creates_calendar_event?: boolean;
    calendar_event_type?: string;
    calendar_duration_minutes?: number;
    service_method?: string;
    service_on_parties?: string[];
    service_proof_required?: boolean;
    estimated_hours?: number;
    is_billable?: boolean;
  },
): Promise<WorkflowTask> {
  const response = await fetch(`${API_BASE}/phases/${phaseId}/tasks`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(dto),
  });
  const data = await parseResponse<{ task: WorkflowTask }>(response);
  return data.task;
}

export async function updateTask(
  taskId: string,
  dto: Partial<WorkflowTask>,
): Promise<WorkflowTask> {
  const response = await fetch(`${API_BASE}/tasks/${taskId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(dto),
  });
  const data = await parseResponse<{ task: WorkflowTask }>(response);
  return data.task;
}

export async function deleteTask(taskId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/tasks/${taskId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  await parseResponse<{ success: boolean }>(response);
}

export async function reorderTasks(phaseId: string, taskIds: string[]): Promise<void> {
  const response = await fetch(`${API_BASE}/phases/${phaseId}/reorder-tasks`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ task_ids: taskIds }),
  });
  await parseResponse<{ success: boolean }>(response);
}

// ─── Task Forms ───

export async function linkForm(
  taskId: string,
  dto: { court_form_id?: string; form_number: string; form_name?: string; is_mandatory?: boolean; filing_sequence?: number },
): Promise<WorkflowTaskForm> {
  const response = await fetch(`${API_BASE}/tasks/${taskId}/forms`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(dto),
  });
  const data = await parseResponse<{ task_form: WorkflowTaskForm }>(response);
  return data.task_form;
}

export async function unlinkForm(taskFormId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/task-forms/${taskFormId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  await parseResponse<{ success: boolean }>(response);
}

// ─── Lifecycle ───

export async function submitForReview(definitionId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/definitions/${definitionId}/submit-review`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  await parseResponse<{ success: boolean }>(response);
}

export async function approveWorkflow(definitionId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/definitions/${definitionId}/approve`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  await parseResponse<{ success: boolean }>(response);
}

export async function publishWorkflow(definitionId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/definitions/${definitionId}/publish`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  await parseResponse<{ success: boolean }>(response);
}

// ─── Audit / Coverage / Export ───

export async function getAuditLog(definitionId: string): Promise<WorkflowAuditLogEntry[]> {
  const response = await fetch(`${API_BASE}/definitions/${definitionId}/audit-log`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const data = await parseResponse<{ logs: WorkflowAuditLogEntry[] }>(response);
  return data.logs;
}

export async function getCoverageMatrix(
  states: string[],
  practiceAreas: string[],
): Promise<CoverageCell[]> {
  const params: Record<string, string | undefined> = {};
  if (states.length > 0) params.states = states.join(',');
  if (practiceAreas.length > 0) params.practice_areas = practiceAreas.join(',');
  const response = await fetch(`${API_BASE}/coverage${toQuery(params)}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const data = await parseResponse<{ matrix: CoverageCell[] }>(response);
  return data.matrix;
}

export async function exportWorkflow(definitionId: string): Promise<Record<string, unknown>> {
  const response = await fetch(`${API_BASE}/definitions/${definitionId}/export`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return parseResponse<Record<string, unknown>>(response);
}

export async function importWorkflow(
  payload: Record<string, unknown>,
): Promise<WorkflowDefinition> {
  const response = await fetch(`${API_BASE}/import`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await parseResponse<{ definition: WorkflowDefinition }>(response);
  return data.definition;
}

// ─── Linking: Definition ↔ Template ───

export interface LinkedTemplate {
  id: string;
  name: string;
  jurisdiction_code: string | null;
  template_kind: string;
  lifecycle_status: string;
  case_subtype: string | null;
  confidence_score: string | null;
  updated_at: string;
}

export interface SourceDefinitionInfo {
  id: string;
  label: string;
  status: string;
  state_code: string;
  practice_area: string;
  case_subtype: string;
  version: number;
  source_authority: string | null;
  published_at: string | null;
  phase_count: number;
  task_count: number;
}

export async function getLinkedTemplates(definitionId: string): Promise<LinkedTemplate[]> {
  const response = await fetch(`${API_BASE}/definitions/${definitionId}/templates`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const data = await parseResponse<{ templates: LinkedTemplate[] }>(response);
  return data.templates;
}

export async function getSourceDefinition(
  stateCode: string,
  practiceArea: string,
  caseSubtype?: string,
): Promise<SourceDefinitionInfo | null> {
  const params: Record<string, string | undefined> = {
    state_code: stateCode,
    practice_area: practiceArea,
    case_subtype: caseSubtype,
  };
  const response = await fetch(`${API_BASE}/source-definition${toQuery(params)}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const data = await parseResponse<{ definition: SourceDefinitionInfo | null }>(response);
  return data.definition;
}

export async function getTemplateCounts(
  definitions: { id: string; state_code: string; practice_area: string; case_subtype: string }[],
): Promise<Record<string, number>> {
  const response = await fetch(`${API_BASE}/template-counts`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ definitions }),
  });
  const data = await parseResponse<{ counts: Record<string, number> }>(response);
  return data.counts;
}

// ─── Helpers ───

export const STATUS_LABELS: Record<WorkflowStatus, string> = {
  draft: 'Draft',
  under_review: 'Under Review',
  validated: 'Validated',
  published: 'Published',
  archived: 'Archived',
};

export const STATUS_COLORS: Record<WorkflowStatus, { bg: string; text: string; dot: string }> = {
  draft: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-300', dot: 'bg-gray-400' },
  under_review: { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-400' },
  validated: { bg: 'bg-lex-accent-soft dark:bg-lex-accent-soft', text: 'text-lex-accent dark:text-lex-accent', dot: 'bg-lex-accent' },
  published: { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-400' },
  archived: { bg: 'bg-rose-50 dark:bg-rose-900/20', text: 'text-rose-700 dark:text-rose-300', dot: 'bg-rose-400' },
};

export function formatStatuteCitation(citation: string): string {
  return citation
    .replace(/\bCal\.\s*Fam\.\s*Code\b/g, 'Cal. Fam. Code')
    .replace(/\bCCP\b/g, 'CCP')
    .replace(/\bCRC\b/g, 'CRC')
    .replace(/\bUCCJEA\b/g, 'UCCJEA')
    .replace(/§§?\s*/g, (m) => m.endsWith(' ') ? m : `${m} `);
}
